import clsx from 'clsx';
import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { tlwTwMerge } from '@trg/utils';
import { IconButton, SliderDots } from '@trg/tlw-ui-component';
import { useProductCardContext } from './ProductCardContext';
import { Swiper, SwiperSlide, type SwiperRef } from 'swiper/react';
import 'swiper/css';

export type ProductCardPreviewProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'children'
>;

export function ProductCardPreview({
  className,
  ...props
}: ProductCardPreviewProps) {
  const { currentSwatch, isVertical } = useProductCardContext();

  const imgList = currentSwatch.imgList ?? [];
  const buttonIconSize = isVertical ? 24 : 16;

  const swiperRef = useRef<SwiperRef>(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!swiperRef.current || !swiperRef.current.swiper) return;
    swiperRef.current.swiper.slideToLoop(0, 0);
    setCurrent(0);
  }, [currentSwatch]);

  return imgList.length <= 1 ? (
    <img
      className={tlwTwMerge('tlw-size-full tlw-object-cover', className)}
      src={currentSwatch.image ?? imgList.at(0)}
      alt={currentSwatch.name}
      {...props}
    />
  ) : (
    <Swiper
      ref={swiperRef}
      className="tlw-h-full"
      spaceBetween={24}
      grabCursor
      nested
      loop
      onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
    >
      {imgList.map((item) => (
        <SwiperSlide key={item}>
          <img
            className={tlwTwMerge('tlw-size-full tlw-object-cover', className)}
            src={item}
            alt={currentSwatch.name}
            {...props}
          />
        </SwiperSlide>
      ))}

      <IconButton
        className="tlw-z-1 tlw-absolute tlw-top-1/2 tlw-left-0 -tlw-translate-y-1/2 tlw-opacity-0 group-hover/product-card:tlw-opacity-100 tlw-hidden lg:tlw-block"
        icon="Left"
        iconSize={buttonIconSize}
        square
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          swiperRef.current?.swiper.slidePrev();
        }}
      />

      <IconButton
        className="tlw-z-1 tlw-absolute tlw-top-1/2 tlw-right-0 -tlw-translate-y-1/2 tlw-opacity-0 group-hover/product-card:tlw-opacity-100 tlw-hidden lg:tlw-block"
        icon="Right"
        iconSize={buttonIconSize}
        square
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          swiperRef.current?.swiper.slideNext();
        }}
      />

      <SliderDots
        className={clsx(
          'tlw-z-1 tlw-absolute tlw-bottom-0 tlw-left-1/2 -tlw-translate-x-1/2 tlw-px-2',
          isVertical && 'lg:tlw-py-3'
        )}
        current={current}
        totalCount={imgList.length}
        size="small"
        onChange={(i) => swiperRef.current?.swiper.slideToLoop(i)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      />
    </Swiper>
  );
}
